import {
  addMinutes,
  isAfter,
  isBefore,
  subMinutes,
  addHours,
  subDays,
  setHours,
  setMinutes,
  addDays,
  isToday
} from 'date-fns'
import { StoreModel } from './utils'
import { CommerceAPI, ShopperBaskets, ShopperCustomers } from 'commerce-api'
import { Status, StatusMap } from '../types/commerce'
import { RootStore } from './RootStore'
import {
  action,
  computed,
  flow,
  flowResult,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import {
  KlarnaPaymentCategories,
  KlarnaSessionResponse,
  KlarnaSetupData,
  ErrorMessageConfig,
  MAX_ACTIVE_ORDERS,
  DELIVERY_PARTNER_STATUS,
  ORDER_DELIVERY_STATUS,
  ORDER_REFUND_STATUS,
  ORDER_STATUS,
  ORDER_UI_STATUS,
  PAYMENT_METHOD_IDS,
  ClearpayCreateCheckoutResponse,
  ClearpaySetupData,
} from '../utils/constants'
import {
  OrderTracking,
  OrderTrackingResponse,
  OrderUIStatus,
} from '../types/store/order'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { DateUtils } from '../utils/date-utils'
import { BasketMergeTypes } from '../types/store/basket'
import { PayPalCheckout } from 'braintree-web'
import { OrderAddress } from '../types/store/basket'
import { KlarnaOptions, KlarnaOrderLine } from '../types/payments'
import { OrderRefundStatus, OrderStatus } from '../types/order-status'

type EditOrderInitResult = { redirect: string, error: Error | string } | null
type ReorderOrderInitResult = { redirect: string } | null
export const FREE_GIFTS_CATEGORY = 'Free Gifts';
export class OrderStore implements StoreModel {
  api: CommerceAPI
  editingOrder: ShopperBaskets.Order | null
  reorderOrder: ShopperBaskets.Order | null
  status: Status
  rootStore: RootStore
  editMode: boolean
  editModeMergeCurrentBasketItems: boolean
  reorderMode: boolean
  reorderModeMergeCurrentBasketItems: boolean
  editOrderDurationExpired: boolean
  recentOrder: ShopperCustomers.Order | null
  populateOrderFromRecentOrder: boolean
  isPaymentLoading: boolean
  paymentErrorMessage: ErrorMessageConfig | null
  paypalInstance: PayPalCheckout | null
  topupAmount: number
  preBookAddress: OrderAddress | null
  amountToPay: number
  klarnaSetupData: KlarnaSetupData
  clearpaySetupData: ClearpaySetupData

  constructor(rootStore: RootStore, initialState = { editingOrder: null, status: StatusMap.IDLE }) {
    this.editingOrder = initialState?.editingOrder
    this.status = initialState?.status
    this.paypalInstance = null
    this.editMode = false
    this.editModeMergeCurrentBasketItems = false
    this.reorderMode = false
    this.reorderModeMergeCurrentBasketItems = false
    this.reorderOrder = null
    this.recentOrder = null
    this.editOrderDurationExpired = false
    this.populateOrderFromRecentOrder = false
    this.topupAmount = 0
    this.preBookAddress = null
    this.amountToPay = 0
    this.klarnaSetupData = {}
    this.clearpaySetupData = {}
    this.isPaymentLoading = false
    this.paymentErrorMessage = null

    makeObservable(this, {
      status: observable,
      editOrderDurationExpired: observable,
      editingOrder: observable,
      editModeMergeCurrentBasketItems: observable,
      editMode: observable,
      reorderOrder: observable,
      reorderMode: observable,
      reorderModeMergeCurrentBasketItems: observable,
      topupAmount: observable,
      preBookAddress: observable,
      amountToPay: observable,
      recentOrder: observable,
      populateOrderFromRecentOrder: observable,
      klarnaSetupData: observable,
      clearpaySetupData: observable,
      isPaymentLoading: observable,
      paymentErrorMessage: observable,
      isPaymentRefunding: computed,
      isPaymentRequired: computed,
      klarnaAmountToPay: computed,
      isRequestProcessing: computed,
      orderEditProductItemsDelta: computed,
      orderEditDeliverySlotChanged: computed,
      refundPaymentMethodsDetails: computed,
      orderEditRemovedProducts: computed,
      orderTotal: computed,
      orderEditOrderTotal: computed,
      orderTotalWithoutBC: computed,
      createKlarnaSession: flow.bound,
      createClearpayCheckout: flow.bound,
      getKlarnaSession: flow.bound,
      updateKlarnaSession: flow.bound,
      getActiveOrders: flow.bound,
      getOrder: flow.bound,
      editOrder: flow.bound,
      prepareOrderEdit: flow.bound,
      initOrderEdit: flow.bound,
      endOrderEdit: flow.bound,
      handleOrderEditPermissions: flow.bound,
      getOrderTrackingData: flow.bound,
      reorder: flow.bound,
      initReorder: flow.bound,
      setRecentOrder: flow.bound,
      setStatus: action.bound,
      updateKlarnaSetupData: action.bound,
      updateClearpaySetupData: action.bound,
      setEditOrderDurationExpired: action.bound,
      setEditModeMergeCurrentBasketItems: action.bound,
      setEditingOrder: action.bound,
      setEditMode: action.bound,
      setReorderOrder: action.bound,
      setReorderMode: action.bound,
      setRecentOrderValue: action.bound,
      setReorderModeMergeCurrentBasketItems: action,
      setPreBookAddress: action.bound,
      setAmountToPay: action.bound,
      setPopulateOrderFromRecentOrder: action.bound,
      setIsPaymentLoading: action.bound,
      setPaymentErrorMessage: action.bound,
      handleIsOrderCanBeShowedAsActive: action.bound,
      validateActiveOrderUIStatus: action.bound,
      validateActiveOrderRefundStatus: action.bound,
      getOrderStatus: action.bound,
      getOrderDeliveryStatusStep: action.bound,
      isMustBeDelivered: action.bound,
      hasDeliveryWindowStarted: action.bound,
      isOrderEditAccessExpired: action.bound,
      getOrderEditAvailabilityExpiryTimeISO: action.bound,
      handleOrderReorderPermissions: action.bound,
      finishReorder: action.bound,
      isOrderEditable: action.bound,
      isOrderCancellable: action.bound,
      handleOrderEditAvailability: action.bound,
      handleOrderCancelAvailability: action.bound,
      isSameDayOrder: action.bound,
      handleOrderActionAvailability: action.bound
    })

    this.api = rootStore.api
    this.rootStore = rootStore

    reaction(
      () => this.rootStore.customerStore.customerInfo?.c_recentOrderId,
      async () => {
        if (!this.rootStore.customerStore.customerInfo?.c_recentOrderId) return
        await this.setRecentOrder()
      },
    )

    reaction(
      () => this.rootStore.basketStore?.basket?.basketId,
      async () => {
        const { basketStore, globalStore } = this.rootStore
        const { basket } = basketStore

        if (!basketStore.loaded) return

        // Don't go further if basket wasn't created for order edit mode.
        if (!basket?.c_orderNoBeingEdited && !basket?.c_orderEditModeInitiatedTime) return

        if (this.editMode) return

        if (!globalStore.isCustomSitePreferenceSet('orderEditModeDurationInMinutes')) {
          await globalStore.getCustomSitePreferenceById('orderEditModeDurationInMinutes')
        }

        const durationMinutes = Number(
          globalStore.customSitePreferences['orderEditModeDurationInMinutes'] ?? 0
        )

        if (!durationMinutes) {
          await basketStore.retrieveParkedCartBack()
          runInAction(() => (this.setEditOrderDurationExpired(true)))
          return
        }

        const editModeExpirationTime = addMinutes(
          new Date(basket.c_orderEditModeInitiatedTime),
          durationMinutes
        )

        const isExpired = isAfter(new Date(), editModeExpirationTime)

        if (isExpired) {
          basketStore.retrieveParkedCartBack()
          runInAction(() => (this.setEditOrderDurationExpired(true)))
          return
        }

        const orderToEdit = await this.getOrder(basket.c_orderNoBeingEdited)
        const editAllowed = await this.handleOrderEditPermissions(orderToEdit)
        const orderHasValidStatus = this.handleValidOrderStatusEditPermission(orderToEdit)

        if (orderHasValidStatus && editAllowed) {
          this.setEditingOrder(orderToEdit)
          this.setEditMode(true)
        } else {
          basketStore.retrieveParkedCartBack()
          runInAction(() => (this.setEditOrderDurationExpired(true)))
        }
      }
    )
  }

  get asJson() {
    return {
      editingOrder: this.editingOrder,
      klarnaSetupData: this.klarnaSetupData,
      clearpaySetupData: this.clearpaySetupData
    }
  }

  get isRequestProcessing() {
    return this.status === StatusMap.PENDING
  }

  get orderEditProductItemsDelta() {
    if (!this.editingOrder || !this.editMode) return {}

    const delta: Record<string, { deltaQty?: number; recentlyAdded?: boolean }> = {}
    const { basketStore } = this.rootStore

    for (const basketItem of basketStore.basket?.productItems || []) {
      const { productId, quantity } = basketItem

      if (!productId) continue

      const originalOrderProduct = this.editingOrder.productItems?.find(
        (productItem) => productItem.productId === productId
      )

      if (!originalOrderProduct) {
        delta[productId] = {
          recentlyAdded: true,
        }
      } else if (
        originalOrderProduct &&
        (quantity || 0) - (originalOrderProduct.quantity || 0) !== 0
      ) {
        delta[productId] = {
          deltaQty: (quantity || 0) - (originalOrderProduct.quantity || 0),
        }
      }
    }

    return delta
  }

  // Total price of the items in the Order
  get orderTotal() {
    const orderTotal = this.rootStore.basketStore?.basket?.orderTotal || 0

    return Number(orderTotal.toFixed(2))
  }

  // Total price to pay for the edit
  get orderEditOrderTotal() {
    if (!this.editMode) return 0

    const total = this.rootStore.basketStore?.basket?.orderEditBalance || 0

    return Number(total.toFixed(2))
  }

  // Total amount left to pay with bonus card subtracted
  // If bonus card not used returns orderTotal/orderEditOrderTotal
  get orderTotalWithoutBC() {
    const bonusCardStatus = this.rootStore.basketStore?.bonusCardStatus
    const total = this.editMode ? this.orderEditOrderTotal : this.orderTotal
    const leftToPay = bonusCardStatus.bonusCardUsed ? bonusCardStatus.leftToPay ?? total : total

    return Number(leftToPay.toFixed(2))
  }

  get orderEditRemovedProducts() {
    if (!this.editingOrder || !this.editMode) return []

    const { basketStore } = this.rootStore
    const { productItems = [] } = this.editingOrder
    const removedProductsCollection = []
    const basketProductItems = [...basketStore.basket?.productItems ?? [], ...basketStore.basket?.bonusLineItems ?? []]

    for (const productItem of productItems) {
      const productInEditCart = basketProductItems?.some(
        ({ productId }) => productId === productItem.productId
      )
      if (productInEditCart) continue

      removedProductsCollection.push(productItem)
    }

    return removedProductsCollection
  }

  get orderEditDeliverySlotChanged() {
    if (!this.editingOrder || !this.editMode) return false

    const { basketStore } = this.rootStore

    return basketStore.basket?.c_deliverySlotId !== this.editingOrder.c_deliverySlotId
  }

  get isPaymentRefunding() {
    return (
      this.editMode &&
      (this.orderTotal || 0) - (this.editingOrder?.orderTotal || 0) < 0
    )
  }

  get isPaymentRequired() {
    return (
      this.editMode ? this.orderEditOrderTotal > 0 : true)
  }

  get klarnaAmountToPay() {
    return Math.round(this.orderTotalWithoutBC * 100)
  }

  get refundPaymentMethodsDetails() {
    if (!this.editingOrder || !this.isPaymentRefunding) return null

    let totalRefundAmount = (this.editingOrder.orderTotal || 0) - (this.orderTotal || 0)

    const details = []

    const { paymentInstruments } = this.editingOrder

    const groupedInstruments = Object.values(
      this.groupPaymentInstrumentsByMethodId(paymentInstruments)
    )

    const sortedInstruments = [...(groupedInstruments || [])].sort((_, next) =>
      next.paymentMethodId === PAYMENT_METHOD_IDS.BONUS_CARD ? 1 : -1
    )

    for (const paymentInstrument of sortedInstruments) {
      const refundAmount =
        totalRefundAmount < (paymentInstrument.amount || 0)
          ? totalRefundAmount
          : paymentInstrument.amount || 0

      details.push({ paymentMethodId: paymentInstrument.paymentMethodId!, refundAmount })
      totalRefundAmount -= refundAmount
    }

    return details.length ? details : null
  }

  updateKlarnaSetupData(value: KlarnaSetupData) {
    const newObj = this.klarnaSetupData
    Object.assign(newObj, value)
    this.klarnaSetupData = newObj
  }

  updateClearpaySetupData(value: ClearpaySetupData) {
    const newObj = this.clearpaySetupData
    Object.assign(newObj, value)
    this.clearpaySetupData = newObj
  }

  setAmountToPay(value: number) {
    this.amountToPay = value
  }

  setPreBookAddress(value: OrderAddress) {
    this.preBookAddress = value
  }

  setStatus(value: Status) {
    this.status = value
  }

  setEditOrderDurationExpired(value: boolean) {
    this.editOrderDurationExpired = value
  }

  setEditingOrder(value: ShopperBaskets.Order | null) {
    this.editingOrder = value
  }

  setEditMode(value: boolean) {
    this.editMode = value
  }

  setReorderOrder(value: ShopperBaskets.Order | null) {
    this.reorderOrder = value
  }

  setReorderMode(value: boolean) {
    this.reorderMode = value
  }

  setEditModeMergeCurrentBasketItems(submitMerging: boolean) {
    this.editModeMergeCurrentBasketItems = submitMerging
  }

  setReorderModeMergeCurrentBasketItems(submitMerging: boolean) {
    this.reorderModeMergeCurrentBasketItems = submitMerging
  }

  setPopulateOrderFromRecentOrder(useRecentOrder: boolean) {
    this.populateOrderFromRecentOrder = useRecentOrder
  }

  setIsPaymentLoading(value: boolean) {
    this.isPaymentLoading = value
  }

  setPaymentErrorMessage(value: ErrorMessageConfig | null) {
    this.paymentErrorMessage = value
  }

  setRecentOrderValue(value: ShopperCustomers.Order | null) {
    this.recentOrder = value
  }

  *createKlarnaSession(orderLines: KlarnaOrderLine[]) {
    try {
      // Remove previous value incase of errors
      this.updateKlarnaSetupData({
        client_token: undefined,
        category: undefined,
        button_rendered: false,
        session_id: undefined,
        order_amount: undefined
      })

      const body = JSON.stringify({
        order_lines: orderLines,
        order_amount: this.klarnaAmountToPay,
        purchase_country: 'GB',
        purchase_currency: 'GBP'
      })
      const response: Response = yield fetch(`${getAppOrigin()}/klarna/createSession`, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { client_token, payment_method_categories, session_id, errMessage }: KlarnaSessionResponse = yield response.json()
      if (!client_token) {
        console.error(`Klarna session not created: ${errMessage ?? ''}`)
        return
      }
      var category = payment_method_categories.find((category) => category.identifier === KlarnaPaymentCategories.KLARNA)
      this.updateKlarnaSetupData({ client_token, category, session_id, order_amount: this.klarnaAmountToPay })
      return response
    } catch (error) {
      throw new Error(`Error creating Klarna Session: ${error}`)
    }
  }

  async *createClearpayCheckout(orderLines: any[]) {
    try {
      const shippingAddress = this.rootStore.basketStore.shippingAddress
      const merchantName = await flowResult(this.rootStore.globalStore.getCustomSitePreferenceById('ClearpayMerchantName')) as string || 'iceland.co.uk'
      // Remove previous value incase of errors
      this.updateClearpaySetupData({
        button_rendered: false,
        order_amount: undefined,
        order_token: undefined
      })

      const customerInfo = this.rootStore.customerStore.customerInfo
      const body = JSON.stringify({
        purchaseCountry: "GB",
        amount: {
          amount: `${this.amountToPay}`,
          currency: "GBP"
        },
        consumer: {
          givenNames: customerInfo?.firstName,
          surname: customerInfo?.lastName,
          email: customerInfo?.email
        },
        items: orderLines,
        merchantReference: "string",
        merchant: {
          name: merchantName,
          redirectConfirmUrl: getAppOrigin(),
          redirectCancelUrl: getAppOrigin()
        },
        shipping: {
          name: shippingAddress?.fullName,
          line1: shippingAddress?.address1,
          ...((shippingAddress?.address2 && shippingAddress?.address2 !== '') && {line2: shippingAddress.address2}),
          area1: shippingAddress?.city,
          region: shippingAddress?.stateCode,
          postcode: shippingAddress?.postalCode,
          countryCode: shippingAddress?.countryCode
        }
      })
      const response: Response = yield fetch(`${getAppOrigin()}/clearpay/createCheckout`, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const { token, message }: ClearpayCreateCheckoutResponse = yield response.json()

      if (!token) {
        console.error(message)
      }

      this.updateClearpaySetupData({ token, button_rendered: true, order_amount: this.amountToPay })

      return response
    } catch (error) {
      this.setIsPaymentLoading(false)
      throw new Error(`Error creating Clearpay checkout: ${error}`)
    }
  }

  *updateKlarnaSession(body: KlarnaOptions) {
    try {

      body.session_id = this.klarnaSetupData.session_id

      const response: Response = yield fetch(`${getAppOrigin()}/klarna/updateSession`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const { success }: { success: boolean } = yield response.json()

      if (success) {
        // If successful update reset the button
        this.updateKlarnaSetupData({ button_rendered: false, order_amount: body.order_amount })
      }
      return response
    } catch (error) {
      throw new Error(`Error creating Klarna Session: ${error}`)
    }
  }

  *getKlarnaSession() {
    const body = JSON.stringify({
      session_id: this.klarnaSetupData.session_id
    })
    const response: Response = yield fetch(`${getAppOrigin()}/klarna/getSession`, {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const jsonResult: KlarnaSessionResponse = yield response.json()

    return jsonResult
  }

  *setRecentOrder(order?: ShopperCustomers.Order) {
    const orderNo = this.rootStore.customerStore.customerInfo?.c_recentOrderId
    if (!orderNo && !order) {
      return
    }
    try {
      if (order) {
        this.setRecentOrderValue(order)
      }
      else if (orderNo && orderNo != this.recentOrder?.orderNo) {
        const recentOrder: ShopperCustomers.Order = yield this.getOrder(orderNo)
        this.setRecentOrderValue(recentOrder)
      }
    } catch (error) {
      console.error('Error retrieving recent order')
    }
    return
  }

    *getOrder(orderNo: string) {
    try {
      this.setStatus(StatusMap.PENDING)
      const result: ShopperCustomers.Order = yield this.api.shopperOrders.getOrder({
        parameters: {
          orderNo: orderNo,
          queryParams: [{
            param: 'c_method',
            value: 'order_summary'
          }]
        },
      })

      this.setStatus(StatusMap.DONE)

      const { productStore } = this.rootStore

      if (result.productItems?.length) {
        const ids = result.productItems
          .map((product) => product.productId)
          .filter((id): id is string => !!id)

        yield productStore.fetchProducts(ids, true)
      }
      return result
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *editOrder(orderNo: string) {
    const action: EditOrderInitResult = yield this.prepareOrderEdit(orderNo)
    return action
  }

  *prepareOrderEdit(orderNo: string) {
    try {
      if (!orderNo) return null

      this.setStatus(StatusMap.PENDING)


      const order: ShopperBaskets.Order = yield this.getOrder(orderNo)
      const editAccessExpired: boolean = yield this.isOrderEditAccessExpired(order)

      if (!this.handleValidOrderStatusEditPermission(order) || editAccessExpired) {
        return null
      }

      const { basketStore } = this.rootStore

      this.setEditingOrder(order)

      // If customer has added products in current basket, open merge basket products UI modal
      // next flow step is handled inside Merge Baskets Modal component.

      if (basketStore.count) {
        basketStore.setBasketMergeType(BasketMergeTypes.Edit)
        basketStore.setInitMergingUI(true)
        return null
      }

      const initResult: EditOrderInitResult | null = yield flowResult(this.initOrderEdit(false, orderNo))

      return initResult
    } catch (error) {
      this.setEditMode(false)
      this.setStatus(StatusMap.ERROR)
      return null
    }
  }

  *initOrderEdit(isMerge = false, orderNo = this.editingOrder?.orderNo ?? null) {
    try {
      if (!orderNo) return null
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/DeliveryOrder-EditOrder?orderid=${orderNo}&ismerge=${isMerge}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: {
        success?: boolean,
        basket: ShopperBaskets.Basket
        errorMessage?: string
      } = yield res.json()

      if (!res.ok || !json?.success) {
        const errorMessage = json?.errorMessage || 'Error during order edit'
        // If edit limit reached, return error
        if (errorMessage === 'edit_limit_reached') return { error: errorMessage }
        throw new Error(`${errorMessage}: ${orderNo}`)
      }

      this.setEditMode(true)

      this.rootStore.basketStore.getOrCreateBasket(json.basket);

      // UI route to redirect user after init completed
      return { redirect: '/basket' }
    } catch (error) {
      this.setEditMode(false)
      this.setStatus(StatusMap.ERROR)
      return error
    }
  }

  *endOrderEdit() {

    if (!this.editingOrder) return null
    const { orderNo } = this.editingOrder

    const res: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/DeliveryOrder-StopEditingOrder?&orderid=${orderNo}`,
      {
        method: 'POST',
        credentials: 'include',
      }
    )

    const json: { success?: boolean, basket: ShopperBaskets.Basket } = yield res.json()

    if (!res.ok || !json?.success) {
      throw new Error('Error during order edit: ' + orderNo)
    }

    this.setEditMode(false)
    this.setEditingOrder(null)

    this.rootStore.basketStore.getOrCreateBasket(json.basket);

    // UI route to redirect user after init completed
    return { redirect: '/basket' }

  }

  *reorder(orderNo: string) {
    try {
      if (!orderNo) return null

      this.setStatus(StatusMap.PENDING)
      const order: ShopperBaskets.Order = yield this.getOrder(orderNo)

      const allowed = this.handleOrderReorderPermissions(order)

      if (!allowed) return null

      const { basketStore } = this.rootStore
      this.setReorderMode(true)
      this.setReorderOrder(order)
      basketStore.setBasketMergeType(null)

      // If customer has added products in current basket, open merge basket products UI modal
      // next flow step is handled inside Merge Baskets Modal component.
      if (basketStore.hasItems) {
        basketStore.setBasketMergeType(BasketMergeTypes.Reorder)
        basketStore.setInitMergingUI(true)
        return null
      }

      const initResult: ReorderOrderInitResult = yield flowResult(this.initReorder(false))

      return initResult
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      console.error(error)
      this.finishReorder()
      return null
    }
  }

  *initReorder(isMerge: boolean) {
    if (!this.reorderOrder) return null

    try {

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/DeliveryOrder-ReOrder?&orderid=${this.reorderOrder.orderNo}&ismerge=${isMerge}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: { success?: boolean, basket: ShopperBaskets.Basket } = yield res.json()

      if (!res.ok || !json?.success) {
        throw new Error('Error during order reorder: ' + this.reorderOrder.orderNo)
      }

      this.rootStore.basketStore.getOrCreateBasket(json.basket);

      this.rootStore.orderStore.setRecentOrderValue(null);

      // UI route to redirect user after init completed
      return { redirect: '/basket' }
    } catch (error) {
      console.log(error)
      return null
    } finally {
      this.finishReorder()
    }
  }

  finishReorder() {
    this.setReorderOrder(null)
    this.setReorderModeMergeCurrentBasketItems(false)
    this.setReorderMode(false)
  }

  isOrderEditable(order: ShopperBaskets.Order) {
    if (!order) return false

    if (this.isSameDayOrder(order)) {
      const disableSameDayEditPref = this.rootStore.globalStore.customSitePreferences['disableOrderEditOnSameDay']
      if (disableSameDayEditPref) {
        return false
      }
    }
    return (order.shipments?.[0]?.shippingMethod?.c_isEditableAfterOrder && order.status !== ORDER_STATUS.CANCELLED)
  }

  isSameDayOrder(order: ShopperBaskets.Order) {
    return order.c_sameDayClassifier
  }

  isOrderCancellable(order: ShopperBaskets.Order) {
    if (!order) return false

    return (order.shipments?.[0]?.shippingMethod?.c_isCancellableAfterOrder && order.status !== ORDER_STATUS.CANCELLED)
  }

  *handleOrderEditPermissions(order: ShopperBaskets.Order) {
    if (!order) return false

    if (!this.isOrderEditable(order)) {
      return false
    }

    const permissionsCustomPreferenceProperties = [
      'deliveryEditOrderExpirationTime',
      'deliveryEditOrderMaxDaysAfterAuth',
    ]
    const { globalStore } = this.rootStore

    const fetchPrefIds = permissionsCustomPreferenceProperties.filter(
      (customPrefId) => !globalStore.isCustomSitePreferenceSet(customPrefId)
    )

    if (fetchPrefIds.length) {
      yield globalStore.getCustomSitePreferencesByIds(fetchPrefIds)
    }

    let allowed = true

    // Checking  if delivery window time is closed or not.
    if (
      globalStore.isCustomSitePreferenceSet('deliveryEditOrderExpirationTime') &&
      typeof globalStore.customSitePreferences['deliveryEditOrderExpirationTime'] === 'number'
    ) {
      const { c_deliverySlotId, c_windowStartTime } = order

      if (!c_deliverySlotId || !c_windowStartTime) {
        allowed = false
      }

      //rolling back 1 day and set midnight
      const deliveryStartWindowDate = setHours(subDays(new Date(c_windowStartTime), 1), 24)

      const closedDeliveryTime = addHours(
        deliveryStartWindowDate,
        globalStore.customSitePreferences['deliveryEditOrderExpirationTime']
      )

      if (!closedDeliveryTime || isBefore(closedDeliveryTime, new Date())) {
        allowed = false
      }
    }

    // Checking if first auth payment date permission expired.
    if (
      globalStore.isCustomSitePreferenceSet('deliveryEditOrderMaxDaysAfterAuth') &&
      typeof globalStore.customSitePreferences['deliveryEditOrderMaxDaysAfterAuth'] === 'number'
    ) {
      const { paymentInstruments } = order

      const [firstAuthPaymentInstrument] = [...(paymentInstruments || [])].sort(
        (prev, next) =>
          new Date(prev.c_creationDate).getTime() - new Date(next.c_creationDate).getTime()
      )

      if (firstAuthPaymentInstrument && firstAuthPaymentInstrument.c_status === 'authorized') {
        const { c_creationDate } = firstAuthPaymentInstrument

        const firstAuthTransactionDate = new Date(c_creationDate)

        // Adding days
        const expDate = addDays(
          firstAuthTransactionDate,
          globalStore.customSitePreferences['deliveryEditOrderMaxDaysAfterAuth']
        )

        if (!expDate || isBefore(expDate, new Date())) {
          allowed = false
        }
      }
    }

    return allowed
  }

  handleOrderReorderPermissions(order: ShopperBaskets.Order) {
    if (!order || this.editMode) return false

    let allowed = true

    if (order.c_isTopUp) {
      allowed = false
    }

    if (order.c_isReorderDisabled) {
      allowed = false
    }

    const allowedStatuses = [ORDER_STATUS.CANCELLED, ORDER_STATUS.COMPLETED] as OrderStatus[]
    const status = order.status as OrderStatus
    const refunded = order.c_data.fulfilment_step === 5
    const partialRefunded = order.c_data.fulfilment_step === 6

    if (!allowedStatuses.includes(status) && !refunded && !partialRefunded) {
      allowed = false
    }

    return allowed
  }

  *getOrderTrackingData(orderNo: string) {
    try {
      const response: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliveryTrackingInfo?orderid=${orderNo}`
      )

      if (!response.ok) {
        throw new Error('Error during fetching Delivery Order Tracking information for: ' + orderNo)
      }

      const json: OrderTrackingResponse = yield response.json()
      return json
    } catch (error) {
      console.log(error)
      return { success: false } as OrderTrackingResponse
    }
  }

  getCustomBookingSlotAttributesFromOrder(order: ShopperBaskets.Order) {
    const extracted: Record<string, unknown> = {}
    const orderSlotAttributes = [
      'c_deliveryPostalCode',
      'c_deliverySlotId',
      'c_deliveryStoreGroup',
      'c_deliveryStoreId',
      'c_isCarrierBagAdded',
      'c_slotExpiryTime',
      'c_slotReservationId',
      'c_windowEndTime',
      'c_windowStartTime',
    ]

    for (const orderSlotAttribute of orderSlotAttributes) {
      if (orderSlotAttribute in order) {
        extracted[orderSlotAttribute] = order[orderSlotAttribute]
      }
    }

    return extracted
  }

  handleValidOrderStatusEditPermission(order: ShopperBaskets.Order) {
    if (!order) return false

    return order.status !== 'cancelled'
  }

  groupPaymentInstrumentsByMethodId(
    paymentInstruments: ShopperBaskets.Order['paymentInstruments']
  ) {
    if (!paymentInstruments?.length) return []

    return paymentInstruments.reduce((grouped, currentPaymentInstrument) => {
      const methodId = currentPaymentInstrument.paymentMethodId!
      if (grouped[methodId]) {
        grouped[methodId] = {
          ...grouped[methodId],
          amount: grouped[methodId].amount + (currentPaymentInstrument.amount || 0),
        }
      } else {
        grouped[methodId] = { ...currentPaymentInstrument }
      }

      return grouped
    }, [])
  }

  *getActiveOrders(orders: ShopperCustomers.Order[]) {
    const nonFailedOrders = orders?.filter((order) => order.status !== ORDER_STATUS.FAILED)

    if (!nonFailedOrders?.length) return []

    let activeOrders: ShopperCustomers.Order[] = nonFailedOrders.filter(
      (order) => order.status !== ORDER_STATUS.CANCELLED && order.status !== ORDER_STATUS.REPLACED
    )

    const currentOrdersPeriod =
      this.rootStore.globalStore.customSitePreferences['currentOrdersPeriod']
    const currentDate = setMinutes(setHours(new Date(), 0), 0)

    const activeOrdersDate =
      typeof currentOrdersPeriod === 'number' ? subDays(currentDate, currentOrdersPeriod) : null

    if (activeOrdersDate) {
      activeOrders = activeOrders.filter(
        (order) => new Date(order.c_windowStartTime) > new Date(activeOrdersDate)
      )
    }

    activeOrders = activeOrders.filter((order) =>
      this.rootStore.orderStore.handleIsOrderCanBeShowedAsActive(order)
    )

    activeOrders = activeOrders.slice(0, MAX_ACTIVE_ORDERS)

    for (const activeOrder of activeOrders) {
      const isDeliveryDayToday = isToday(new Date(activeOrder.c_windowStartTime))
      const orderInternalStatus = activeOrder.status === ORDER_STATUS.COMPLETED

      if (activeOrder.orderNo && orderInternalStatus && isDeliveryDayToday) {
        const trackingData: OrderTrackingResponse = yield this.getOrderTrackingData(
          activeOrder.orderNo
        )

        if (trackingData.success) {
          activeOrder.c_trackingData = { ...trackingData.data, orderNo: activeOrder.orderNo }
        }
      }
    }

    return activeOrders
  }

  handleIsOrderCanBeShowedAsActive(order: ShopperCustomers.Order) {
    if (order.c_isTopUp || order.c_isPreBook) {
      return false
    }

    return this.validateActiveOrderUIStatus(order) && this.validateActiveOrderRefundStatus(order)
  }

  validateActiveOrderUIStatus(order: ShopperCustomers.Order) {
    const orderUIStatus = this.getOrderStatus(order)

    return [
      ORDER_UI_STATUS.PLACED,
      ORDER_UI_STATUS.PICKED,
      ORDER_UI_STATUS.FINDING_A_RIDER,
      ORDER_UI_STATUS.RIDER_ASSIGNED,
      ORDER_UI_STATUS.PREPARING_FOR_DISPATCH,
      ORDER_UI_STATUS.OUT_FOR_DELIVERY
    ].includes(orderUIStatus)
  }

  validateActiveOrderRefundStatus(order: ShopperCustomers.Order) {
    let refundStatus: OrderRefundStatus = ORDER_REFUND_STATUS.NOT_REFUNDED

    if (order.c_pickedStatus) {
      if (order.c_refunded || Number(order.c_amountPaid) === 0) {
        refundStatus = ORDER_REFUND_STATUS.REFUNDED
      }
      // @todo - Handle PARTIAL_REFUND
    }
    return refundStatus === ORDER_REFUND_STATUS.NOT_REFUNDED
  }


  getOrderStatus(order: ShopperCustomers.Order) {
    const {
      c_data,
      c_trackingData,
      c_isTopUp,
      c_isPreBook,
      c_isRapidDelivery,
      c_deliveryPartnerStatus,
    } = order || {}

    let orderStatus = c_data?.fulfilment_status || c_data?.fulfilmentStatus || ORDER_UI_STATUS.PLACED

    if ((c_isTopUp && orderStatus === ORDER_UI_STATUS.COMPLETED) || c_isPreBook) {
      return orderStatus
    }

    const mustBeDelivered = this.isMustBeDelivered(order)
    const withinDeliveryWindow = !mustBeDelivered && this.hasDeliveryWindowStarted(order)

    if (c_isRapidDelivery && order.status !== ORDER_STATUS.CANCELLED) {
      if (!order.c_pickedStatus) {
        orderStatus = ORDER_DELIVERY_STATUS.PLACED
      } else if (DELIVERY_PARTNER_STATUS[c_deliveryPartnerStatus]) {
        orderStatus = DELIVERY_PARTNER_STATUS[c_deliveryPartnerStatus]
      } else {
        orderStatus = ORDER_DELIVERY_STATUS.FINDING_A_RIDER
      }
    } else if (orderStatus === ORDER_UI_STATUS.COMPLETED && !mustBeDelivered) {
      orderStatus = withinDeliveryWindow ? ORDER_UI_STATUS.OUT_FOR_DELIVERY : ORDER_UI_STATUS.PICKED
    }

    if (!c_isRapidDelivery && c_trackingData && c_trackingData?.deliveryStatus) {
      const statusKey: 'FAILED' | 'COMPLETED' | 'OUT_FOR_DELIVERY' =
        c_trackingData?.deliveryStatus?.toUpperCase()?.split(' ')?.join('_')
      if (ORDER_DELIVERY_STATUS[statusKey]) {
        orderStatus = ORDER_DELIVERY_STATUS[statusKey]
      }
    }

    const statusMap: Record<string, string> = {
      [ORDER_DELIVERY_STATUS.PLACED]: ORDER_UI_STATUS.PLACED,
      [ORDER_DELIVERY_STATUS.PICKED]: ORDER_UI_STATUS.PICKED,
      [ORDER_DELIVERY_STATUS.FINDING_A_RIDER]: ORDER_UI_STATUS.FINDING_A_RIDER,
      [ORDER_DELIVERY_STATUS.RIDER_ASSIGNED]: ORDER_UI_STATUS.RIDER_ASSIGNED,
      [ORDER_DELIVERY_STATUS.OUT_FOR_DELIVERY]: ORDER_UI_STATUS.OUT_FOR_DELIVERY,
      [ORDER_DELIVERY_STATUS.COMPLETED]: ORDER_UI_STATUS.DELIVERED,
      [ORDER_DELIVERY_STATUS.FAILED]: ORDER_UI_STATUS.DELIVERY_UNSUCCESSFUL,
      [ORDER_DELIVERY_STATUS.CANCELLED]: ORDER_UI_STATUS.CANCELLED,
    }

    const orderStatusMap = orderStatus.toUpperCase().replaceAll(' ', '_')
    const mappedStatus = statusMap[orderStatusMap]
    if (mappedStatus) {
      orderStatus = mappedStatus
    } else if (
      ![
        ORDER_UI_STATUS.PREPARING_FOR_DISPATCH,
        ORDER_UI_STATUS.REFUNDED,
        ORDER_UI_STATUS.PARTIALLY_REFUNDED,
      ].includes(orderStatus) &&
      mustBeDelivered
    ) {
      orderStatus = ORDER_UI_STATUS.DELIVERED;
    }

    return orderStatus
  }

  getOrderDeliveryStatusStep(status: OrderUIStatus) {
    // Map our Order Statuses to Delivery Status Keys for delivery updates
    const statusMap: Record<string, string> = {
      [ORDER_UI_STATUS.PLACED]: ORDER_DELIVERY_STATUS.PLACED,
      [ORDER_UI_STATUS.PICKED]: ORDER_DELIVERY_STATUS.PICKED,
      [ORDER_UI_STATUS.PREPARING_FOR_DISPATCH]: ORDER_DELIVERY_STATUS.PICKED,
      [ORDER_UI_STATUS.FINDING_A_RIDER]: ORDER_DELIVERY_STATUS.FINDING_A_RIDER,
      [ORDER_UI_STATUS.RIDER_ASSIGNED]: ORDER_DELIVERY_STATUS.RIDER_ASSIGNED,
      [ORDER_UI_STATUS.OUT_FOR_DELIVERY]: ORDER_DELIVERY_STATUS.OUT_FOR_DELIVERY,
      [ORDER_UI_STATUS.DELIVERY_UNSUCCESSFUL]: ORDER_DELIVERY_STATUS.FAILED,
      [ORDER_UI_STATUS.DELIVERED]: ORDER_DELIVERY_STATUS.COMPLETED,
      [ORDER_UI_STATUS.COMPLETED]: ORDER_DELIVERY_STATUS.COMPLETED,
      [ORDER_UI_STATUS.PARTIALLY_REFUNDED]: ORDER_DELIVERY_STATUS.COMPLETED,
      [ORDER_UI_STATUS.REFUNDED]: ORDER_DELIVERY_STATUS.COMPLETED,
      [ORDER_UI_STATUS.CANCELLED]: ORDER_DELIVERY_STATUS.CANCELLED
    }
    return statusMap[status] || ORDER_DELIVERY_STATUS.PLACED
  }

  isMustBeDelivered(order: ShopperCustomers.Order) {
    if (order.c_isRapidDelivery) return true
    if (!order.c_windowEndTime || !order.c_pickedStatus) return false

    const currentDate = new Date()
    const deliveryDate = new Date(order.c_windowEndTime)

    return isAfter(currentDate, deliveryDate)
  }

  hasDeliveryWindowStarted(order: ShopperCustomers.Order) {
    if (order.c_isRapidDelivery) return true
    if (!order.c_windowStartTime || !order.c_pickedStatus) return false

    return !isAfter(new Date(order.c_windowStartTime), new Date())
  }

  isOrderEditAccessExpired(order: ShopperCustomers.Order | ShopperBaskets.Order) {
    if (order.c_isTopUp) return true

    const expiryDate = this.getOrderEditAvailabilityExpiryTimeISO(order)

    let expired = expiryDate == null

    if (!expired && expiryDate) {
      const { c_deliverySlotId, c_windowStartTime } = order

      if (!c_deliverySlotId || !c_windowStartTime) {
        expired = true
      }

      if (isBefore(new Date(expiryDate), new Date())) {
        expired = true
      }
    }

    return expired
  }

  getOrderEditAvailabilityExpiryTimeISO(order: ShopperCustomers.Order | ShopperBaskets.Order) {
    const { globalStore } = this.rootStore

    let editExpiryDate: Date

    if (order.c_sameDayClassifier && order.c_sameDayHardCutOff) {
      editExpiryDate = new Date(order.c_sameDayHardCutOff)
    } else {
      const { c_deliverySlotId, c_windowStartTime } = order

      const deliveryStartWindowDate = new Date(c_windowStartTime)

      if (
        !c_deliverySlotId ||
        !c_windowStartTime ||
        !DateUtils.validateDate(deliveryStartWindowDate)
      ) {
        return null
      }

      editExpiryDate = subDays(deliveryStartWindowDate, 1)

      editExpiryDate = setHours(editExpiryDate, 24)
      editExpiryDate = setMinutes(editExpiryDate, 0)

      if (
        globalStore.customSitePreferences['deliveryEditOrderExpirationTime'] &&
        typeof globalStore.customSitePreferences['deliveryEditOrderExpirationTime'] === 'number'
      )
        editExpiryDate = addHours(
          editExpiryDate,
          globalStore.customSitePreferences['deliveryEditOrderExpirationTime']
        )
    }

    return DateUtils.validateDate(editExpiryDate) ? editExpiryDate?.toISOString() : null
  }

  isDeliveryTrackingMapDisplayed(order: ShopperCustomers.Order & { c_trackingData?: OrderTracking }) {
    if (!order.c_trackingData || order.c_isRapidDelivery) return false

    const { globalStore } = this.rootStore
    const data = order?.c_trackingData

    let inStartRange = false
    let inEndRange = false

    if (
      globalStore.isCustomSitePreferenceSet('numberOfStops') &&
      typeof globalStore.customSitePreferences['numberOfStops'] === 'number'
    ) {
      if (data.numberOfStops > globalStore.customSitePreferences['numberOfStops']) return false
    }

    const { c_windowStartTime } = order
    let showMapStartDate: Date = DateUtils.utcToZonedDate(c_windowStartTime)

    if (DateUtils.validateDate(showMapStartDate)) {
      if (
        globalStore.isCustomSitePreferenceSet('showMapMinutesBeforeDeliverySlotStart') &&
        typeof globalStore.customSitePreferences['showMapMinutesBeforeDeliverySlotStart'] ===
        'number'
      ) {
        showMapStartDate = subMinutes(
          showMapStartDate,
          globalStore.customSitePreferences['showMapMinutesBeforeDeliverySlotStart']
        )
      }

      const currentDate = DateUtils.current()

      if (isAfter(currentDate, showMapStartDate)) {
        inStartRange = true
      }
    }

    const { c_windowEndTime } = order
    let showMapEndDate: Date = DateUtils.utcToZonedDate(c_windowEndTime)

    if (DateUtils.validateDate(showMapEndDate)) {
      if (
        globalStore.isCustomSitePreferenceSet('showMapMinutesAfterDeliverySlotEnd') &&
        typeof globalStore.customSitePreferences['showMapMinutesAfterDeliverySlotEnd'] === 'number'
      ) {
        showMapEndDate = addMinutes(
          showMapEndDate,
          globalStore.customSitePreferences['showMapMinutesAfterDeliverySlotEnd']
        )

        const currentDate = DateUtils.current()

        if (isBefore(currentDate, showMapEndDate)) {
          inEndRange = true
        }
      }
    }

    return inStartRange && inEndRange
  }

  handleOrderReorderAvailability(order: ShopperCustomers.Order) {
    const orderUIStatus = order.c_data?.fulfilment_status || order.c_data?.fulfilmentStatus
    const isAcceptedStatus = [
        ORDER_UI_STATUS.COMPLETED,
        ORDER_UI_STATUS.CANCELLED,
        ORDER_UI_STATUS.REFUNDED,
        ORDER_UI_STATUS.PARTIALLY_REFUNDED,
      ].includes(orderUIStatus)

    return !order?.c_isTopUp &&
      !order?.c_isPreBook &&
      !order?.c_isReorderDisabled
      && isAcceptedStatus

  }

  handleOrderActionAvailability(order: ShopperCustomers.Order) {
    const isCreatedOrNew = order.status.toLowerCase() === ORDER_STATUS.CREATED || order.status.toLowerCase() === ORDER_STATUS.NEW
    const hasPickedStatus = Object.prototype.hasOwnProperty.call(order, 'c_pickedStatus')

    return (
      isCreatedOrNew &&
      hasPickedStatus &&
      !order.c_pickedStatus &&
      !this.editMode &&
      !this.isOrderEditAccessExpired(order)
    )
  }

  handleOrderEditAvailability(order: ShopperCustomers.Order) {
    return this.handleOrderActionAvailability(order) && this.isOrderEditable(order)
  }

  handleOrderCancelAvailability(order: ShopperCustomers.Order) {
    return this.handleOrderActionAvailability(order) && this.isOrderCancellable(order)
  }

}
